import { ReactElement, useMemo } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useParams } from 'react-router-dom'
import { useElementHistory } from '@hooks'
import { AxiosError } from 'axios'
import { find, toNumber } from 'lodash-es'
import { v4 } from 'uuid'
import { TabPanel } from '@mui/lab'
import { Typography } from '@mui/material'
import { Stack } from '@mui/system'
import {
  wallSettingsOf,
  WallSettings,
} from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeleteButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import {
  useEditElementStore,
  useControlStore,
  useControlStoreApi,
  useModelStore,
} from '@editorStores'
import { useBlockScene, useResultsInvalidation } from '@editorHooks'
import {
  AssemblyAssignmentForm,
  CollapsibleAssemblyBoxSection,
  CollapsibleCopyBoxSection,
  CollapsibleOpeningsBoxSection,
  CollapsibleProblemsBoxSection,
  CollapsibleSettingsBoxSection,
  CollapsibleSplitBoxSection,
} from '@editorComponents'
import { useModelInvalidation, useStructuralPlanningFormEsc } from '@structuralPlanningHooks'
import { getModel, getElementLoads } from '@queries'
import { createWall, deleteWall, updateWall as updateWallRequest } from '@mutations'
import { buildErrorMessage } from 'src/constants'
import CopyToOtherStoreys from '../../../MultiEdit/components/CopyToOtherStoreys'
import PlanSettingsForm from '../../../PlanSettingsForm'
import SplitWallForm from '../../../SplitWallForm'
import WallOpeningsSection from '../../../WallOpeningsSection'
import { GeometryForm, WallFormBase } from './components'

const toNewWallPosition = (wall: ShapeObject): NewWallPosition => {
  const [start, end] = wall.shape.points

  return {
    new_start: {
      x: start.x,
      y: start.y,
    },
    new_end: {
      x: end.x,
      y: end.y,
    },
    placement: wall.placement || 'External',
  }
}

interface Props {
  selectedElement: string
  geometryEditable?: boolean
  storeySelected: boolean
  onClose: () => void
}

const WallForm = ({
  selectedElement,
  geometryEditable = true,
  storeySelected,
  onClose,
}: Props): ReactElement | null => {
  const { walls } = useModelStore(state => state.model)

  const removeWall = useModelStore(state => state.removeWall)
  const updateWall = useModelStore(state => state.updateWall)
  const controlStoreApi = useControlStoreApi()
  const clearIntelligentDependentWallOriginals = useControlStore(
    state => state.clearIntelligentDependentWallOriginals,
  )

  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const { enqueueSnackbar } = useSnackbar()
  const { projectId }: { projectId?: string } = useParams()

  const invalidateModel = useModelInvalidation()
  const invalidateResults = useResultsInvalidation()

  const wall = useMemo(() => {
    return find(walls, ['guid', selectedElement]) as Wall
  }, [walls, selectedElement])

  const elementType: ElementTypes = wall?.placement === 'Internal' ? 'inner_walls' : 'outer_walls'

  const queryClient = useQueryClient()

  const dependencies = useMemo(() => [selectedElement], [selectedElement])

  const restoreIntelligentDependentWalls = () => {
    const originals = controlStoreApi.getState().intelligentDependentWallOriginals
    originals.forEach((original: ShapeObject) => {
      updateWall({
        ...original,
        is_translated: undefined,
        is_updated: undefined,
        is_local: undefined,
      })
    })
    clearIntelligentDependentWallOriginals()
  }

  const { setStale: setWallStale, reset } = useElementHistory({
    element: wall,
    getCurrentElement: () => {
      const walls = useModelStore.getState().model.walls
      return find(walls, ['guid', selectedElement]) as Wall
    },
    removeElement: wall => removeWall(wall.guid),
    resetElement: wall => {
      restoreIntelligentDependentWalls()
      updateWall({
        ...wall,
        is_translated: undefined,
        is_updated: undefined,
        is_local: undefined,
      })
    },
    dependencies,
  })

  useStructuralPlanningFormEsc(() => {
    // wall has to be saved otherwise remove
    if (wall?.is_local) {
      removeWall(wall.guid)
    }
    onClose()
  }, !!selectedElement)

  // MUTATIONS

  const { mutateAsync, isLoading } = useMutation(
    (wall: Wall) =>
      createWall.request(projectId, {
        guid: wall.guid,
        p1: wall.shape.points[0],
        p2: wall.shape.points[1],
        placement: wall.placement,
        storey_idx: toNumber(wall.storey),
        ...(wall.label ? { label: wall.label } : {}),
        ...wallSettingsOf(wall),
      }),
    {
      onSuccess: async () => {
        setWallStale(wall)
        // The refetched model already contains the new wall in store shape (`inner_walls` /
        // `outer_walls` element_type, string storey). Merging the raw response DTO on top of it
        // would overwrite `element_type` with the backend's generic "walls", which no visibility
        // filter knows, so the wall would be hidden until the next reload.
        await invalidateModel(projectId as string)
        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich gespeichert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern der Wand'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleDelete, isLoading: isDeleting } = useMutation(
    (wallGuid: string) => deleteWall.request(projectId, wallGuid),
    {
      onSuccess: async () => {
        await invalidateModel(projectId as string)
        setActiveElement(null)
        removeWall(wall.guid)
        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich gelöscht', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Löschen der Wand'), {
          variant: 'error',
        })
      },
    },
  )

  const { mutateAsync: handleUpdate, isLoading: isUpdating } = useMutation(
    async (submittedWall: Wall) => {
      await updateWallRequest.request(
        projectId,
        submittedWall.guid,
        toNewWallPosition(submittedWall),
      )

      const intelligentOriginals = controlStoreApi.getState().intelligentDependentWallOriginals
      const currentWalls = useModelStore.getState().model.walls

      for (const original of intelligentOriginals) {
        const dependent = find(currentWalls, ['guid', original.guid]) as ShapeObject | undefined
        if (!dependent?.is_updated) continue
        await updateWallRequest.request(projectId, dependent.guid, toNewWallPosition(dependent))
      }

      return submittedWall
    },
    {
      onSuccess: async (submittedWall: Wall) => {
        clearIntelligentDependentWallOriginals()
        setWallStale(submittedWall)
        await invalidateModel(projectId as string)
        setActiveElement(null)

        await queryClient.invalidateQueries(getElementLoads.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))
        await queryClient.invalidateQueries(getModel.getKey(projectId))

        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich verändert', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Ändern der Wand'), {
          variant: 'error',
        })
      },
    },
  )

  useBlockScene(isDeleting || isLoading || isUpdating)

  // Handlers for split wall form
  const handleCreateWall = async (
    wallData: {
      p1: { x: number; y: number }
      p2: { x: number; y: number }
      storey_idx: number
      placement: WallPlacement
      openings?: Opening[]
    } & WallSettings,
  ) => {
    const newWall = await createWall.request(projectId, {
      guid: v4(),
      p1: wallData.p1,
      p2: wallData.p2,
      storey_idx: wallData.storey_idx,
      placement: wallData.placement as 'Internal',
      positionPlanSettings: wallData.positionPlanSettings,
      certificateSettings: wallData.certificateSettings,
    })
    await invalidateModel(projectId as string)
    invalidateResults(projectId as string)
    return newWall
  }

  const handleDeleteWall = async (wallGuid: string) => {
    await deleteWall.request(projectId, wallGuid)
    removeWall(wallGuid)
    await invalidateModel(projectId as string)
    invalidateResults(projectId as string)
  }

  // HOTKEYS

  useHotkeys(
    'enter',
    () => {
      if (wall.is_translated || wall.is_updated) {
        handleUpdate(wall)
      }
    },
    { enabled: wall && !wall.is_local && (wall.is_translated || !!wall.is_updated) },
    [wall, updateWall],
  )

  if (!storeySelected) return null

  if (!wall)
    return (
      <Box p={1} border={1} borderColor="grey.200" borderRadius={1} mb={2}>
        <Typography mb={1}>Sie haben nun die Möglichkeit neue Wände einzuzeichnen</Typography>
      </Box>
    )

  if (wall.is_local) {
    return (
      <Stack direction="column" spacing={1} p={1}>
        <GeometryForm
          wall={wall}
          handleSubmit={submittedWall => mutateAsync({ ...wallSettingsOf(wall), ...submittedWall })}
          handleDelete={() => {
            setActiveElement(null)
            removeWall(wall.guid)
          }}
          isSaveable
          isSubmitting={isLoading}
          showCreateLabel
        />
      </Stack>
    )
  }

  return (
    <WallFormBase initialTabValue="geometry">
      <TabPanel value="geometry" sx={{ padding: 1 }}>
        {geometryEditable ? (
          <>
            <Stack direction="column" spacing={0.5}>
              <GeometryForm
                wall={wall}
                handleSubmit={() => handleUpdate(wall)}
                handleDelete={() => handleDelete(wall.guid)}
                isDeleting={isDeleting}
                isSaveable
                isSubmitting={isUpdating}
                isResettable={true}
                handleReset={reset}
                onClose={onClose}
              />

              <CollapsibleAssemblyBoxSection defaultExpanded={true}>
                <Stack direction="column" spacing={1}>
                  <AssemblyAssignmentForm
                    elementType={elementType}
                    selectedElements={[selectedElement]}
                    onClose={onClose}
                    showSelectedElementsNumber={false}
                    autoFocus={false}
                  />
                </Stack>
              </CollapsibleAssemblyBoxSection>

              <CollapsibleOpeningsBoxSection defaultExpanded={false}>
                <WallOpeningsSection wall={wall} mode="2D" />
              </CollapsibleOpeningsBoxSection>

              <CollapsibleProblemsBoxSection elementGuid={selectedElement} defaultExpanded={true} />

              <CollapsibleSettingsBoxSection defaultExpanded={false}>
                <PlanSettingsForm element={wall} setStale={setWallStale} />
              </CollapsibleSettingsBoxSection>

              <CollapsibleSplitBoxSection label="Teilen" defaultExpanded={false}>
                <SplitWallForm
                  element_guid={selectedElement}
                  handleCreate={handleCreateWall}
                  handleDelete={handleDeleteWall}
                />
              </CollapsibleSplitBoxSection>
              {wall.storey && (
                <CollapsibleCopyBoxSection
                  label="Kopieren"
                  defaultExpanded={false}
                  dataCy="wall-form-copy-to-storey"
                >
                  <CopyToOtherStoreys selectedElements={[selectedElement]} storey={wall.storey} />
                </CollapsibleCopyBoxSection>
              )}
            </Stack>
          </>
        ) : (
          <DeleteButton
            onClick={() => handleDelete(wall.guid)}
            loading={isDeleting}
            data-cy="delete-wall-btn"
          >
            Löschen
          </DeleteButton>
        )}
      </TabPanel>
    </WallFormBase>
  )
}

export default WallForm
